/**
 * All page copy lives here, in one place, so content can be edited without
 * touching component code.
 *
 * Content is fully filled in. The Gallery section is an intentional
 * placeholder — swap `gallery.placeholders` for real project cards once you
 * have finished work to show. One optional TODO remains in `footer`
 * (business address/registration), only needed if your jurisdiction
 * requires it on a public site.
 */

export const nav = [
  { label: 'About', href: '#about' },
  { label: 'What We Do', href: '#services' },
  { label: 'R&D', href: '#custom-development' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Contact', href: '#contact' },
];

export const hero = {
  eyebrow: 'BeamOn',
  headline: "See what's next, from above.",
  subheadline:
    'BeamOn is a commercial drone services company based in Southwestern Ontario — turning aerial photography, mapping, and inspection data into clear answers for real estate, construction, agriculture, and infrastructure teams. Certified pilots and professional-grade sensors mean the imagery and data you get back are ready to act on, not just look at.',
  primaryCta: { label: 'Request a Quote', href: '#contact' },
  secondaryCta: { label: 'See our services', href: '#services' },
};

export const about = {
  eyebrow: 'About Us',
  heading: 'Who we are',
  paragraphs: [
    "BeamOn exists because the best information about a property, a job site, or a piece of land often isn't visible from the ground. We fly commercial drones to capture the photos, video, maps, and data that help our clients make faster, better-informed decisions — before they list a property, break ground, or sign off on a milestone.",
    "Every flight is planned around the outcome our client needs, not just the footage: GPS-guided flight paths for mapping work, thermal sensors for inspections, cinematic settings for marketing content. We fly under Transport Canada's RPAS rules and hand back data in the formats our clients' teams already use — no new software to learn.",
  ],
  highlights: [
    { label: 'Focus', value: 'Aerial imaging & drone data' },
    { label: 'Based in', value: 'Southwestern Ontario' },
  ],
};

export type Service = {
  title: string;
  description: string;
};

export const services = {
  eyebrow: 'What We Do',
  heading: 'Services',
  items: [
    {
      title: 'Aerial Photography & Videography',
      description:
        'Cinematic stills and video for marketing, brand content, and events — captured in ultra-HD and edited to match your brand.',
    },
    {
      title: 'Real Estate & Property Marketing',
      description:
        'Listing photos, walkthrough video, and orbit shots that help residential and commercial properties sell faster and stand out online.',
    },
    {
      title: 'Construction Progress Monitoring',
      description:
        'Scheduled recurring flights turned into dated photo sets and timelapse — documentation your team, lenders, and stakeholders can rely on.',
    },
    {
      title: 'Thermal & Infrared Inspection',
      description:
        "Thermal imaging finds what's invisible to the eye: roof and building-envelope heat loss, solar panel faults, and electrical hotspots — caught on camera before they become expensive repairs.",
    },
    {
      title: '3D Mapping & Photogrammetry',
      description:
        'Orthomosaic maps, 3D models, and volumetric measurements for surveying, stockpiles, and site planning — the numbers your team needs without putting a crew on the ground.',
    },
    {
      title: 'Agriculture & Land Surveying',
      description:
        'NDVI crop health imaging and land assessment that turn a flyover into a map you can act on this season.',
    },
  ] satisfies Service[],
};

export type CustomCapability = {
  title: string;
  description: string;
};

export const customDevelopment = {
  eyebrow: 'Custom Development',
  heading: 'Software, AI & R&D',
  description:
    "The six services above are ready to book. This is different: when a project calls for something that doesn't exist yet, we design and build it — custom capture software, computer vision models, mapping pipelines wired into your own systems, or early-stage research. Scoped per project, not a fixed package.",
  capabilities: [
    {
      title: 'Custom Flight & Capture Software',
      description:
        'Automated mission planning, custom onboard capture logic, and integrations with the tools your team already runs.',
    },
    {
      title: 'Visual Object Recognition',
      description:
        'Computer vision models trained to detect, count, and track what matters in your footage — vehicles, equipment, wildlife, crop stress, and more.',
    },
    {
      title: 'Mapping & Data Pipelines',
      description:
        'Automated orthomosaic and point-cloud pipelines that turn raw flight data into structured outputs your systems can use directly.',
    },
    {
      title: 'Applied Research & Prototyping',
      description:
        'Sensor evaluation, payload testing, and pilot studies for teams validating what drone data can do before committing to a full deployment.',
    },
  ] satisfies CustomCapability[],
  cta: { label: 'Discuss a custom project', href: '#contact' },
};

export type GalleryPlaceholder = {
  label: string;
  caption: string;
};

export const gallery = {
  eyebrow: 'Our Work',
  heading: 'The gallery is warming up',
  description:
    "We're building out a portfolio of recent flights — real estate listings, job site progress, thermal scans, and mapped sites. Sample projects are landing here soon.",
  placeholders: [
    { label: 'Real Estate', caption: 'Listing & property shoots' },
    { label: 'Construction', caption: 'Progress & timelapse' },
    { label: 'Thermal', caption: 'Inspection scans' },
    { label: 'Mapping', caption: '3D & orthomosaic' },
  ] satisfies GalleryPlaceholder[],
  cta: { label: 'Ask about a recent project', href: '#contact' },
};

export const contact = {
  eyebrow: 'Contact',
  heading: "Let's talk",
  description:
    "Tell us about your project — a property, a job site, or a piece of land — and what you need from the air. We'll follow up with a quote and a realistic timeline.",
  email: 'hello@beamon.org',
  // Cloudflare Worker endpoint that receives the form POST (see apps/worker).
  // Set VITE_CONTACT_ENDPOINT in .env.local for local dev.
  submitEndpoint: import.meta.env.VITE_CONTACT_ENDPOINT ?? '/api/contact',
  turnstileSiteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '',
};

export const footer = {
  companyName: 'BeamOn',
  // TODO: real address / registration details, only if your jurisdiction requires displaying one.
  tagline: 'Aerial photography, mapping, and inspection data — flown right.',
  socials: [
    // { label: 'LinkedIn', href: 'https://linkedin.com/company/TODO' },
  ],
};
